import { useMemo, useState } from "react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { 
  useListReportSettings, 
  type ReportSetting,
  type AdjustedMarginReportOptionMonthFilters 
} from "@/lib/api/reportSettings";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, Send } from "lucide-react";
import { CreateReportSettingModal } from "./components/CreateReportSettingModal";
import { UpdateReportSettingModal } from "./components/UpdateReportSettingModal";
import { SendDormanMarginCallModal } from "./components/SendDormanMarginCallModal";

export function BrokerageReportSettingsDashboard() {
  const listResult = useListReportSettings();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDormanMarginCallModalOpen, setIsDormanMarginCallModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<ReportSetting | null>(null);

  const settingsData = useMemo(() => {
    const allSettings = listResult?.data?.data || [];
    
    // Filter for only dorman adjusted_margin_report_option_month_filters
    return allSettings.filter(
      (setting: ReportSetting) => 
        setting.provider_key === "dorman" && 
        setting.setting_key === "adjusted_margin_report_option_month_filters"
    );
  }, [listResult]);

  const columns = useMemo((): DataTableColumn<ReportSetting>[] => [
    {
      id: "provider_key",
      accessorKey: "provider_key",
      header: "Provider",
      cell: ({ row }) => {
        const providerKey = row.original.provider_key;
        const displayName = providerKey.charAt(0).toUpperCase() + providerKey.slice(1);
        return (
          <Badge variant="outline">
            {displayName}
          </Badge>
        );
      },
    },
    {
      id: "setting_key",
      accessorKey: "setting_key",
      header: "Setting",
      cell: ({ row }) => {
        const settingKey = row.original.setting_key;
        // Convert snake_case to Title Case
        const displayName = settingKey
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return (
          <span className="text-sm">
            {displayName}
          </span>
        );
      },
    },
    {
      id: "filter_type",
      accessorKey: "setting_value.filter_type",
      header: "Filter Type",
      cell: ({ row }) => {
        const value = row.original.setting_value as AdjustedMarginReportOptionMonthFilters;
        const filterType = value?.filter_type;
        const displayName = filterType 
          ? filterType.charAt(0).toUpperCase() + filterType.slice(1)
          : "N/A";
        return (
          <Badge 
            variant={filterType === "whitelist" ? "default" : "secondary"}
          >
            {displayName}
          </Badge>
        );
      },
    },
    {
      id: "commodity_type",
      accessorKey: "setting_value.commodity_type",
      header: "Commodity",
      cell: ({ row }) => {
        const value = row.original.setting_value as AdjustedMarginReportOptionMonthFilters;
        return (
          <span className="text-sm font-mono">
            {value?.commodity_type || "All"}
          </span>
        );
      },
    },
    {
      id: "row_type",
      accessorKey: "setting_value.row_type",
      header: "Row Type",
      cell: ({ row }) => {
        const value = row.original.setting_value as AdjustedMarginReportOptionMonthFilters;
        const rowType = value?.row_type;
        const displayName = rowType 
          ? rowType.charAt(0).toUpperCase() + rowType.slice(1)
          : "All";
        return (
          <span className="text-sm">
            {displayName}
          </span>
        );
      },
    },
    {
      id: "option_months",
      accessorKey: "setting_value.option_months",
      header: "Option Months",
      cell: ({ row }) => {
        const value = row.original.setting_value as AdjustedMarginReportOptionMonthFilters;
        const months = value?.option_months || [];
        
        if (months.length === 0) {
          return <span className="text-muted-foreground text-sm">None</span>;
        }
        
        return (
          <div className="flex flex-wrap gap-1">
            {months.map((month, idx) => (
              <Badge key={idx} variant="outline" className="font-mono text-xs">
                {month}
              </Badge>
            ))}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => {
        return (
          <span className="text-sm text-muted-foreground">
            {moment.tz(row.original.created_at, CommonDestinationTimezone).format("YYYY-MM-DD HH:mm")}
          </span>
        );
      },
    },
    {
      id: "updated_at",
      accessorKey: "updated_at",
      header: "Updated",
      cell: ({ row }) => {
        return (
          <span className="text-sm text-muted-foreground">
            {moment.tz(row.original.updated_at, CommonDestinationTimezone).format("YYYY-MM-DD HH:mm")}
          </span>
        );
      },
    },
  ], []);

  const isLoading = listResult.isLoading;

  const handleRowClick = (row: ReportSetting) => {
    setSelectedSetting(row);
    setIsUpdateModalOpen(true);
  };

  const handleSuccess = () => {
    listResult.refetch();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Report Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure brokerage report filtering settings
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setIsDormanMarginCallModalOpen(true)}
          >
            <Send className="mr-2 h-4 w-4" />
            Send Manual Dorman Report
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Setting
          </Button>
        </div>
      </div>

      <DataTable
        stickyHeaders
        tableClassName="h-[calc(70vh-200px)] overflow-y-scroll"
        data={settingsData}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        enableColumnVisibility
        enableExport
        enableColumnResizing
        exportFilename={(data) => `brokerage-report-settings-${new Date().toISOString().split('T')[0]}.csv`}
        filterStyle="select"
        filterMode="internal"
        defaultSorting={[{ id: "updated_at", desc: true }]}
        emptyMessage={isLoading ? "Loading..." : "No report settings found"}
        rowClickable
        onRowClick={handleRowClick}
      />

      {/* Modals */}
      <CreateReportSettingModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleSuccess}
      />

      <UpdateReportSettingModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedSetting(null);
        }}
        onSuccess={handleSuccess}
        reportSetting={selectedSetting}
      />

      <SendDormanMarginCallModal
        isOpen={isDormanMarginCallModalOpen}
        onClose={() => setIsDormanMarginCallModalOpen(false)}
      />
    </div>
  );
}
